import { useEffect, useState, type ReactElement } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Radio, Loader2, WifiOff } from 'lucide-react';
import type { ConnectionState } from '@/hooks/useSignalR';

interface LiveTrackingToggleProps {
  enabled: boolean;
  onToggle: () => void;
  activeCount: number;
  /**
   * Realtime hub state from `useSignalR`. When omitted or `live`, no pill renders.
   * `reconnecting` renders only after a 2s debounce so wifi blips don't flash.
   */
  connectionState?: ConnectionState;
}

// Only surface `reconnecting` after this many ms of staying in that state.
const RECONNECTING_DEBOUNCE_MS = 2_000;

export function LiveTrackingToggle({
  enabled,
  onToggle,
  activeCount,
  connectionState,
}: LiveTrackingToggleProps) {
  return (
    <div className="flex items-center gap-2">
      <Button
        variant={enabled ? 'default' : 'outline'}
        size="sm"
        onClick={onToggle}
        className={cn(
          'gap-2 rounded-full backdrop-blur-sm transition-all duration-200',
          enabled
            ? 'bg-gray-900 hover:bg-gray-800 text-white shadow-[0_4px_16px_rgba(0,0,0,0.2)]'
            : 'bg-white/95 hover:bg-white shadow-[0_2px_12px_rgba(0,0,0,0.1)] hover:shadow-md'
        )}
      >
        {enabled ? (
          <>
            {/* Pulsing green dot */}
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
            </span>
            <span className="font-semibold text-xs uppercase tracking-wide">
              Live
            </span>
            <Badge
              className={cn(
                'border-transparent bg-green-600 text-white',
                'text-[10px] px-1.5 py-0 ml-0.5'
              )}
            >
              {activeCount} active
            </Badge>
          </>
        ) : (
          <>
            <Radio className="h-4 w-4" />
            <span className="text-xs">Live</span>
          </>
        )}
      </Button>
      <ConnectionPill state={connectionState} />
    </div>
  );
}

function ConnectionPill({ state }: { state?: ConnectionState }) {
  // Debounce `reconnecting`: SignalR's first retry fires within ~2s, so most
  // transient drops resolve before we'd bother the user with a pill.
  const [debouncedReconnecting, setDebouncedReconnecting] = useState(false);

  useEffect(() => {
    if (state !== 'reconnecting') {
      setDebouncedReconnecting(false);
      return;
    }
    const t = setTimeout(() => setDebouncedReconnecting(true), RECONNECTING_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [state]);

  if (!state || state === 'live') return null;
  if (state === 'reconnecting' && !debouncedReconnecting) return null;

  const { label, icon, className } = PILL_PRESENTATION[state];
  return (
    <Badge
      variant="outline"
      role="status"
      aria-live="polite"
      className={cn(
        'gap-1.5 rounded-full py-1 font-medium shadow-[0_2px_8px_rgba(0,0,0,0.08)]',
        className,
      )}
    >
      {icon}
      {label}
    </Badge>
  );
}

const PILL_PRESENTATION: Record<
  Exclude<ConnectionState, 'live'>,
  { label: string; icon: ReactElement; className: string }
> = {
  connecting: {
    label: 'Connecting…',
    icon: <Loader2 className="h-3 w-3 animate-spin" />,
    className: 'bg-gray-100 text-gray-700 border-gray-200',
  },
  reconnecting: {
    label: 'Reconnecting…',
    icon: <Loader2 className="h-3 w-3 animate-spin" />,
    className: 'bg-warning/10 text-warning border-warning/30',
  },
  offline: {
    label: 'Offline',
    icon: <WifiOff className="h-3 w-3" />,
    className: 'bg-destructive/10 text-destructive border-destructive/30',
  },
};
